import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    alpha,
    CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SendIcon from "@mui/icons-material/Send";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { isBursarUser } from "../../../utils/staffRoles";
import {
    fetchTermBudgetOverview,
    upsertTermBudget,
    reviewTermBudget,
    fetchSchoolMaterialRequests,
    reviewMaterialRequest,
} from "../../../utils/termBudgetApi";
import Popup from "../../../components/Popup";

const formatMWK = (n) => "MWK " + Number(n || 0).toLocaleString();

function statusChip(status) {
    if (status === "Approved") return <Chip size="small" color="success" label="Approved" />;
    if (status === "PendingApproval") return <Chip size="small" color="warning" label="Pending approval" />;
    if (status === "Rejected") return <Chip size="small" color="error" label="Rejected" />;
    return <Chip size="small" variant="outlined" label="Draft" />;
}

function requestStatusChip(status) {
    if (status === "Approved") return <Chip size="small" color="success" label="Approved" />;
    if (status === "Fulfilled") return <Chip size="small" color="info" label="Fulfilled" />;
    if (status === "Rejected") return <Chip size="small" color="error" label="Rejected" />;
    return <Chip size="small" color="warning" label="Pending" />;
}

function progressTone(pct) {
    if (pct > 90) return "error";
    if (pct > 70) return "warning";
    return "success";
}

export default function TermBudgetManagement() {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);
    const isBursar = isBursarUser(currentUser);
    const actorId = currentUser?._id || currentUser?.id;
    const actorPayload = useMemo(
        () => (isBursar ? { teacherId: actorId } : { adminId: actorId }),
        [isBursar, actorId],
    );

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [requests, setRequests] = useState([]);
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);

    const [budgetDialog, setBudgetDialog] = useState(false);
    const [formCategoryId, setFormCategoryId] = useState("");
    const [formAmount, setFormAmount] = useState("");
    const [formNotes, setFormNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const [reviewTarget, setReviewTarget] = useState(null);
    const [reviewNotes, setReviewNotes] = useState("");
    const [reviewCost, setReviewCost] = useState("");
    const [rejectReason, setRejectReason] = useState("");

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const [ov, reqs] = await Promise.all([
                fetchTermBudgetOverview(schoolId, actorPayload),
                fetchSchoolMaterialRequests(schoolId, { ...actorPayload, currentTerm: true }),
            ]);
            setOverview(ov);
            setRequests(Array.isArray(reqs) ? reqs : []);
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load term budget data.");
            setShowPopup(true);
        } finally {
            setLoading(false);
        }
    }, [schoolId, actorPayload]);

    useEffect(() => {
        load();
    }, [load]);

    const periodLabel = overview?.period
        ? [overview.period.term, overview.period.academicYear].filter(Boolean).join(" · ")
        : "Current term";

    const handleSaveBudget = async (submitForApproval = false) => {
        const amt = parseFloat(formAmount);
        if (!formCategoryId || !Number.isFinite(amt) || amt <= 0) return;
        setSaving(true);
        try {
            await upsertTermBudget({
                schoolId,
                categoryId: formCategoryId,
                budgetedAmount: amt,
                notes: formNotes,
                submitForApproval,
                ...actorPayload,
            });
            setBudgetDialog(false);
            setFormCategoryId("");
            setFormAmount("");
            setFormNotes("");
            setMessage(submitForApproval ? "Budget submitted for approval." : "Budget saved.");
            setShowPopup(true);
            load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not save budget.");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const handleBudgetAction = async (id, action) => {
        try {
            await reviewTermBudget(id, { action, ...actorPayload });
            setMessage(action === "approve" ? "Budget approved." : "Budget updated.");
            setShowPopup(true);
            load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not update budget.");
            setShowPopup(true);
        }
    };

    const handleRequestReview = async (action) => {
        if (!reviewTarget) return;
        const id = reviewTarget.id || reviewTarget._id;
        setSaving(true);
        try {
            await reviewMaterialRequest(id, {
                action,
                rejectionReason: rejectReason,
                adminNotes: reviewNotes,
                estimatedCost: reviewCost,
                ...actorPayload,
            });
            setReviewTarget(null);
            setReviewNotes("");
            setReviewCost("");
            setRejectReason("");
            setMessage("Request updated.");
            setShowPopup(true);
            load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not review request.");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    if (loading && !overview) {
        return (
            <Stack alignItems="center" py={8}>
                <CircularProgress />
            </Stack>
        );
    }

    const summary = overview?.summary || {};
    const budgets = overview?.budgets || [];
    const missing = overview?.missingCategories || [];

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[100]} 20%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="lg">
                <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />

                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="overline" color="text.secondary" fontWeight={600}>
                            Finance · restricted
                        </Typography>
                        <Typography variant="h5" fontWeight={800}>
                            Term budgets
                        </Typography>
                        <Chip size="small" label={periodLabel} color="primary" variant="outlined" sx={{ mt: 1 }} />
                    </Box>
                    {tab === 0 ? (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setBudgetDialog(true)}>
                            Set category budget
                        </Button>
                    ) : null}
                </Stack>

                <Alert severity="info" sx={{ mb: 2 }}>
                    Only the school director and bursar can view amounts. Teachers submit material needs separately without seeing budget figures.
                </Alert>

                <Paper elevation={0} sx={{ mb: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                        <Tab label={`Budgets (${budgets.length})`} />
                        <Tab label={`Material requests (${summary.pendingMaterialRequests ?? 0} pending)`} />
                    </Tabs>
                </Paper>

                {tab === 0 ? (
                    <>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, borderRadius: 2 }} variant="outlined">
                                    <Typography variant="caption" color="text.secondary">Total budget</Typography>
                                    <Typography variant="h6" fontWeight={800}>{formatMWK(summary.totalBudget)}</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, borderRadius: 2 }} variant="outlined">
                                    <Typography variant="caption" color="text.secondary">Spent (approved)</Typography>
                                    <Typography variant="h6" fontWeight={800} color="warning.main">{formatMWK(summary.totalSpent)}</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, borderRadius: 2 }} variant="outlined">
                                    <Typography variant="caption" color="text.secondary">Remaining</Typography>
                                    <Typography variant="h6" fontWeight={800} color={summary.totalRemaining < 0 ? "error.main" : "success.main"}>
                                        {formatMWK(summary.totalRemaining)}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, borderRadius: 2 }} variant="outlined">
                                    <Typography variant="caption" color="text.secondary">Awaiting approval</Typography>
                                    <Typography variant="h6" fontWeight={800}>{summary.pendingApprovalCount ?? 0}</Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Stack spacing={2}>
                            {budgets.map((b) => {
                                const pct = b.budgetedAmount > 0 ? (b.totalSpent / b.budgetedAmount) * 100 : 0;
                                const tone = progressTone(pct);
                                const id = b.id || b._id;
                                return (
                                    <Paper key={id} variant="outlined" sx={{ p: 2 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography fontWeight={700}>{b.category?.name || "Category"}</Typography>
                                                {statusChip(b.status)}
                                            </Stack>
                                            <Stack direction="row" spacing={1}>
                                                {b.status === "Draft" || b.status === "Rejected" ? (
                                                    <Button size="small" startIcon={<SendIcon />} onClick={() => handleBudgetAction(id, "submit")}>
                                                        Submit
                                                    </Button>
                                                ) : null}
                                                {b.status === "PendingApproval" ? (
                                                    <>
                                                        <Button size="small" color="success" startIcon={<CheckCircleOutlineIcon />} onClick={() => handleBudgetAction(id, "approve")}>
                                                            Approve
                                                        </Button>
                                                        <Button size="small" color="error" startIcon={<CancelOutlinedIcon />} onClick={() => handleBudgetAction(id, "reject")}>
                                                            Reject
                                                        </Button>
                                                    </>
                                                ) : null}
                                            </Stack>
                                        </Stack>
                                        <LinearProgress variant="determinate" value={Math.min(100, pct)} color={tone} sx={{ height: 8, borderRadius: 4, mb: 1.5 }} />
                                        <Grid container spacing={2}>
                                            <Grid item xs={4}><Typography variant="caption" color="text.secondary">Budgeted</Typography><Typography fontWeight={600}>{formatMWK(b.budgetedAmount)}</Typography></Grid>
                                            <Grid item xs={4}><Typography variant="caption" color="text.secondary">Spent</Typography><Typography fontWeight={600}>{formatMWK(b.totalSpent)}</Typography></Grid>
                                            <Grid item xs={4}><Typography variant="caption" color="text.secondary">Remaining</Typography><Typography fontWeight={600} color={b.remaining < 0 ? "error.main" : undefined}>{formatMWK(b.remaining)}</Typography></Grid>
                                        </Grid>
                                    </Paper>
                                );
                            })}
                        </Stack>

                        {missing.length > 0 ? (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    Categories without a budget this term
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                    {missing.map((c) => (
                                        <Chip
                                            key={c.id || c._id}
                                            label={c.name}
                                            variant="outlined"
                                            onClick={() => {
                                                setFormCategoryId(c.id || c._id);
                                                setBudgetDialog(true);
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        ) : null}
                    </>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Teacher</TableCell>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Qty</TableCell>
                                    <TableCell>Urgency</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Est. cost</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {requests.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                            No material requests for this term.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    requests.map((row) => {
                                        const id = row.id || row._id;
                                        return (
                                            <TableRow key={id} hover>
                                                <TableCell>{row.teacher?.name || "—"}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>{row.title}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{row.description}</Typography>
                                                </TableCell>
                                                <TableCell>{row.itemType}</TableCell>
                                                <TableCell>{row.quantity}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={row.urgency} color={row.urgency === "Urgent" ? "error" : "default"} variant="outlined" />
                                                </TableCell>
                                                <TableCell>{requestStatusChip(row.status)}</TableCell>
                                                <TableCell align="right">{row.estimatedCost != null ? formatMWK(row.estimatedCost) : "—"}</TableCell>
                                                <TableCell align="right">
                                                    {row.status === "Pending" || row.status === "Approved" ? (
                                                        <Button size="small" onClick={() => setReviewTarget(row)}>Review</Button>
                                                    ) : "—"}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Container>

            <Dialog open={budgetDialog} onClose={() => !saving && setBudgetDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Set term budget</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Category</InputLabel>
                            <Select label="Category" value={formCategoryId} onChange={(e) => setFormCategoryId(e.target.value)}>
                                {(overview?.categories || []).map((c) => (
                                    <MenuItem key={c.id || c._id} value={c.id || c._id}>{c.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            required
                            type="number"
                            label="Budget amount"
                            value={formAmount}
                            onChange={(e) => setFormAmount(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start">MWK</InputAdornment> }}
                        />
                        <TextField fullWidth multiline minRows={2} label="Notes (optional)" value={formNotes} onChange={(e) => setFormNotes(e.target.value)} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBudgetDialog(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={() => handleSaveBudget(false)} disabled={saving}>Save draft</Button>
                    <Button variant="contained" onClick={() => handleSaveBudget(true)} disabled={saving}>Submit for approval</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(reviewTarget)} onClose={() => !saving && setReviewTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Review material request</DialogTitle>
                <DialogContent>
                    {reviewTarget ? (
                        <Stack spacing={2} sx={{ pt: 1 }}>
                            <Typography variant="body2"><strong>{reviewTarget.title}</strong> — {reviewTarget.teacher?.name}</Typography>
                            <TextField fullWidth label="Internal notes" value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} multiline minRows={2} />
                            <TextField
                                fullWidth
                                type="number"
                                label="Estimated cost (internal)"
                                value={reviewCost}
                                onChange={(e) => setReviewCost(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start">MWK</InputAdornment> }}
                            />
                            <TextField fullWidth label="Rejection reason (shown to teacher)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReviewTarget(null)} disabled={saving}>Cancel</Button>
                    <Button color="error" onClick={() => handleRequestReview("reject")} disabled={saving}>Reject</Button>
                    <Button color="success" onClick={() => handleRequestReview("approve")} disabled={saving}>Approve</Button>
                    <Button variant="contained" onClick={() => handleRequestReview("fulfill")} disabled={saving}>Mark fulfilled</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
